import { Trans } from "@lingui/macro";
import { ApiEpBilling } from "@smp/api";
import {
  UiBox,
  UiCard,
  UiCardActions,
  UiCardContent,
  UiDialogResponsive,
  UiGrid,
  UiHelper,
  UiLink,
  UiTypography,
} from "@smp/ui";
import { testAppendAttr, textCapitalize } from "@smp/utils";
import format from "date-fns/format";
import formatDistance from "date-fns/formatDistance";
import parseISO from "date-fns/parseISO";
import { useSnackbar } from "notistack";
import { useMemo } from "react";
import { useMutation } from "react-query";

import { HelperAdapterLink } from "@/components/helpers/helper_adapter_link";
import { useDialog } from "@/hooks/use_dialog";
import { useEpBillingProductPlanProductList } from "@/hooks/use_ep_billing_product_plan_product";
import { useFailureTranslator } from "@/hooks/use_failure_translator";
import { useHumanizer } from "@/hooks/use_humanizer";
import { useMePermissions } from "@/hooks/use_me_permissions";
import { DialogPurchase } from "@/screens/app/components/dialogs/dialog_purchase";
import { useContextApp } from "@/screens/app/context/app";

import { Pages } from "../../_shared";

export const DialogWorld = ({
  onClose,
  pagesLimit,
  pagesUsed,
}: {
  onClose: () => void;
  pagesLimit: number | null;
  pagesUsed: number;
}) => {
  const {
    state: {
      me,
      planActive,
      planLast,
      plansubscriptionActive,
      plansubscriptionLast,
    },
  } = useContextApp();
  const mePermissions = useMePermissions();

  const {
    close: dialogPurchaseClose,
    isOpen: isDialogPurchaseOpen,
    open: dialogPurchaseOpen,
  } = useDialog();

  const { productPlanProductList } = useEpBillingProductPlanProductList({
    req: {
      query: {
        billing_policy_id: me.billing_policy_id,
        plan_id: planLast?.id,
      },
    },
    useQueryOptions: { enabled: !!planLast },
  });

  const { failureTranslationGet } = useFailureTranslator();

  const { humanizeBillingPlanId } = useHumanizer();

  const { enqueueSnackbar } = useSnackbar();

  const { isLoading: isMutatePurchaseLoading, mutate: mutatePurchase } =
    useMutation(ApiEpBilling.Product.PlanProduct.purchase, {
      onError: (clause) => {
        enqueueSnackbar(failureTranslationGet(clause), { variant: "error" });
      },
    });

  const product = useMemo(() => {
    if (!productPlanProductList) {
      return undefined;
    }

    return [...productPlanProductList]
      .sort((pp1, pp2) => {
        if (
          pp1.is_recurring_payment_allowed !== pp2.is_recurring_payment_allowed
        ) {
          return pp2.is_recurring_payment_allowed ? 1 : -1;
        } else if (pp1.period_units !== pp2.period_units) {
          return pp2.period_units - pp1.period_units;
        } else {
          return pp2.period_length - pp1.period_length;
        }
      })
      .at(0);
  }, [productPlanProductList]);

  const handlePurchaseFreeForeign = () => {
    if (!product) return;

    mutatePurchase({
      body: {
        allow_internal_funds: false,
        payment_provider: "paypal",
        payment_type: "single",
      },
      path: { id: product.id },
    });
  };

  return (
    <>
      <UiDialogResponsive
        fullWidth
        maxWidth="sm"
        onClose={onClose}
        open
        title={<Trans>My Plan</Trans>}
      >
        <UiGrid container spacing={2}>
          <UiGrid item xs={12} sm={4}>
            <UiCard color="primary" fullHeight fullWidth>
              <UiCardContent>
                <UiTypography gutterBottom>
                  {planLast ? (
                    humanizeBillingPlanId(planLast.id) ||
                    textCapitalize(planLast.name)
                  ) : (
                    <Trans>Not selected</Trans>
                  )}
                </UiTypography>
              </UiCardContent>
              <UiCardActions>
                <UiTypography>
                  <UiLink
                    color="inherit"
                    component={HelperAdapterLink}
                    onClick={onClose}
                    to="/billing/plans"
                    {...testAppendAttr("id", "change_billing_plan")}
                  >
                    <Trans>Change Subscription Plan</Trans>
                  </UiLink>
                </UiTypography>
              </UiCardActions>
            </UiCard>
          </UiGrid>
          {plansubscriptionLast && (
            <UiGrid item xs={12} sm={4}>
              {!!plansubscriptionActive ? (
                <UiCard color="info" fullHeight fullWidth>
                  <UiCardContent>
                    <UiTypography>
                      <Trans>Paid up to</Trans>
                    </UiTypography>
                    <UiTypography>
                      {format(
                        parseISO(plansubscriptionLast.period_end),
                        "d MMMM yyyy",
                      )}{" "}
                      (
                      {formatDistance(
                        parseISO(plansubscriptionLast.period_end),
                        Date.now(),
                      )}
                      )
                    </UiTypography>
                  </UiCardContent>
                  {mePermissions.renew_plan && (
                    <UiCardActions>
                      <UiTypography>
                        <UiLink
                          color="inherit"
                          onClick={dialogPurchaseOpen}
                          {...testAppendAttr("id", "renew_billing_plan")}
                        >
                          <Trans>Renew</Trans>
                        </UiLink>
                      </UiTypography>
                    </UiCardActions>
                  )}
                </UiCard>
              ) : (
                <UiCard color="warning" fullHeight fullWidth>
                  <UiCardContent>
                    <UiTypography>
                      <Trans>Expired</Trans>
                    </UiTypography>
                    <UiTypography>
                      {format(
                        parseISO(plansubscriptionLast?.period_end),
                        "d MMMM yyyy",
                      )}
                    </UiTypography>
                  </UiCardContent>
                  {planLast &&
                    !planLast.is_trial &&
                    !["free", "free_foreign"].includes(planLast.id) && (
                      <UiCardActions>
                        <UiLink
                          color="primary"
                          onClick={dialogPurchaseOpen}
                          variant="subtitle1"
                          {...testAppendAttr("id", "renew_billing_plan")}
                        >
                          <Trans>Renew</Trans>
                        </UiLink>
                      </UiCardActions>
                    )}
                  {planLast &&
                    !planLast.is_trial &&
                    planLast.id === "free_foreign" &&
                    !planActive && (
                      <UiCardActions>
                        <UiLink
                          color="primary"
                          onClick={
                            isMutatePurchaseLoading
                              ? undefined
                              : handlePurchaseFreeForeign
                          }
                          variant="subtitle1"
                          {...testAppendAttr("id", "renew_billing_plan")}
                        >
                          {isMutatePurchaseLoading ? (
                            <Trans>Loading</Trans>
                          ) : (
                            <Trans>Renew</Trans>
                          )}
                        </UiLink>
                      </UiCardActions>
                    )}
                </UiCard>
              )}
            </UiGrid>
          )}
          {plansubscriptionLast && (
            <UiGrid item xs={12} sm={4}>
              <UiBox display="flex" flexDirection="column" height="100%">
                <UiBox flexGrow={1} />
                <UiBox
                  alignItems="center"
                  display="flex"
                  flexDirection="row"
                  height={48}
                  paddingRight={1}
                >
                  <UiBox
                    alignItems="center"
                    display="flex"
                    flexDirection="row"
                    flexGrow={1}
                  >
                    <UiTypography>
                      <Trans>In use</Trans>
                    </UiTypography>
                    <UiHelper
                      title={
                        <UiTypography>
                          <Trans>
                            The number of social pages that support simultaneous
                            planned publications. This set of pages can be
                            changed at any time throughout the subscription
                            period. Our system automatically lists only active
                            pages with scheduled publications.
                          </Trans>
                        </UiTypography>
                      }
                    />
                  </UiBox>
                  <UiBox flexGrow={1}>
                    <UiTypography align="right">{pagesUsed}</UiTypography>
                  </UiBox>
                </UiBox>
                <UiBox
                  alignItems="center"
                  borderTop="solid 1px #EEE"
                  display="flex"
                  flexDirection="row"
                  height={48}
                  paddingRight={1}
                >
                  <UiBox flexGrow={1}>
                    <UiTypography>
                      <Trans>Total</Trans>
                    </UiTypography>
                  </UiBox>
                  <UiBox flexGrow={1}>
                    <UiTypography align="right">
                      {pagesLimit ?? "-"}
                    </UiTypography>
                  </UiBox>
                </UiBox>
                <UiBox flexGrow={1} />
              </UiBox>
            </UiGrid>
          )}
          <Pages onClose={onClose} />
        </UiGrid>
      </UiDialogResponsive>
      {product && isDialogPurchaseOpen && (
        <DialogPurchase onClose={dialogPurchaseClose} open product={product} />
      )}
    </>
  );
};
